// calculators/balance-transfer/src/calculator.ts
// UI logic for the public Balance Transfer Calculator.
// Reads inputs, validates, runs the shared payoffSimulation engine, renders the
// net-savings result + a 2-series balance trajectory chart, and fires GA4 events.

import {
  monthlyRateFromDailyCompounding,
  simulateMinimumPayoff,
} from "../../../../shared/payoffSimulation";
import {
  renderMultiSeriesChart,
  seriesFromMonths,
  clearChart,
  type ChartSeries,
} from "../../_shared/multiSeriesChart";

const CALC_ID = "balance_transfer";
const CHART_ID = "tc-chart";

type FieldName =
  | "amount"
  | "currentApr"
  | "promoApr"
  | "postApr"
  | "fee";

interface Inputs {
  amount: number;
  currentApr: number; // decimal
  promoApr: number; // decimal
  postApr: number; // decimal (falls back to currentApr if blank)
  feePct: number; // decimal (e.g. 0.03)
  promoMonths: number;
  promoType: "true0" | "deferred";
}

interface ValidationResult {
  valid: boolean;
  values?: Inputs;
  errors: Partial<Record<FieldName, string>>;
}

const DEBOUNCE_MS = 300;
const GA_INPUT_DEBOUNCE_MS = 1000;
const MAX_AMOUNT = 100000;
const MAX_APR_PCT = 50;
const MAX_FEE_PCT = 10;

// ── DOM / i18n / GA helpers ──────────────────────────────────────────────
function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function t(
  key: string,
  fallback: string,
  params?: Record<string, string | number>
): string {
  const w = window as unknown as {
    i18next?: { t: (k: string, p?: Record<string, string | number>) => string };
  };
  if (w.i18next && typeof w.i18next.t === "function") {
    const val = w.i18next.t(key, { defaultValue: fallback, ...(params || {}) });
    return typeof val === "string" ? val : fallback;
  }
  if (params) {
    return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) =>
      params[k] === undefined ? `{{${k}}}` : String(params[k])
    );
  }
  return fallback;
}

function gtag(...args: unknown[]): void {
  const w = window as unknown as { gtag?: (...a: unknown[]) => void };
  if (typeof w.gtag === "function") w.gtag(...args);
}

// Current site locale — same source/priority as js/i18n.js and store-tracking.js
// (i18next active language → <html lang> → ?lang= → 'en'). Sent on every event.
function getLang(): string {
  const SUPPORTED = ["en", "es", "fr", "en-GB", "en-AU", "en-CA", "fr-CA"];
  try {
    const w = window as unknown as {
      i18next?: { language?: string };
      getSiteLang?: () => string;
    };
    if (typeof w.getSiteLang === "function") return w.getSiteLang();
    if (w.i18next && typeof w.i18next.language === "string" && w.i18next.language) {
      return w.i18next.language;
    }
    const docLang = document.documentElement.getAttribute("lang");
    if (docLang && SUPPORTED.indexOf(docLang) !== -1) return docLang;
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && SUPPORTED.indexOf(param) !== -1) return param;
  } catch {
    /* fall through */
  }
  return "en";
}

function formatCurrency(n: number): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

// ── Parsing ──────────────────────────────────────────────────────────────
function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s,$]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

// Percent string → decimal. Returns null on blank/invalid.
function parsePct(raw: string): number | null {
  const cleaned = raw.replace(/[\s,%]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return n / 100;
}

function readRadio(name: string): string | null {
  const el = document.querySelector<HTMLInputElement>(
    `input[name="${name}"]:checked`
  );
  return el ? el.value : null;
}

function readInputs(): Record<FieldName, string> {
  return {
    amount: ($("tc-input-amount") as HTMLInputElement | null)?.value || "",
    currentApr: ($("tc-input-currentApr") as HTMLInputElement | null)?.value || "",
    promoApr: ($("tc-input-promoApr") as HTMLInputElement | null)?.value || "",
    postApr: ($("tc-input-postApr") as HTMLInputElement | null)?.value || "",
    fee: ($("tc-input-fee") as HTMLInputElement | null)?.value || "",
  };
}

// ── Validation ───────────────────────────────────────────────────────────
function validate(raw: Record<FieldName, string>): ValidationResult {
  const errors: Partial<Record<FieldName, string>> = {};

  const amount = parseAmount(raw.amount);
  if (amount === null || amount <= 0) {
    errors.amount = t(
      "calculators.balanceTransfer.error_amount_required",
      "Enter a transfer amount greater than 0."
    );
  } else if (amount > MAX_AMOUNT) {
    errors.amount = t(
      "calculators.balanceTransfer.error_amount_too_large",
      "Transfer amount must be $100,000 or less."
    );
  }

  const currentApr = parsePct(raw.currentApr);
  if (currentApr === null || currentApr < 0) {
    errors.currentApr = t(
      "calculators.balanceTransfer.error_current_apr_required",
      "Enter your current APR."
    );
  } else if (currentApr * 100 > MAX_APR_PCT) {
    errors.currentApr = t(
      "calculators.balanceTransfer.error_apr_too_large",
      "APR must be 50% or less."
    );
  }

  const promoApr = parsePct(raw.promoApr);
  if (promoApr === null || promoApr < 0) {
    errors.promoApr = t(
      "calculators.balanceTransfer.error_promo_apr_required",
      "Enter the promo APR (0 for a 0% offer)."
    );
  } else if (promoApr * 100 > MAX_APR_PCT) {
    errors.promoApr = t(
      "calculators.balanceTransfer.error_apr_too_large",
      "APR must be 50% or less."
    );
  }

  // Post-promo APR: blank → falls back to current APR.
  let postApr = parsePct(raw.postApr);
  if (postApr !== null) {
    if (postApr < 0) {
      errors.postApr = t(
        "calculators.balanceTransfer.error_post_apr_invalid",
        "Enter a valid post-promo APR."
      );
    } else if (postApr * 100 > MAX_APR_PCT) {
      errors.postApr = t(
        "calculators.balanceTransfer.error_apr_too_large",
        "APR must be 50% or less."
      );
    }
  }
  if (postApr === null && currentApr !== null) postApr = currentApr;

  const feePct = parsePct(raw.fee);
  if (feePct === null || feePct < 0) {
    errors.fee = t(
      "calculators.balanceTransfer.error_fee_required",
      "Enter the transfer fee (0 if none)."
    );
  } else if (feePct * 100 > MAX_FEE_PCT) {
    errors.fee = t(
      "calculators.balanceTransfer.error_fee_too_large",
      "Transfer fee must be 10% or less."
    );
  }

  const promoMonths = Number(readRadio("promoPeriod") || "12");
  const promoTypeRaw = readRadio("promoType") || "true0";
  const promoType: "true0" | "deferred" =
    promoTypeRaw === "deferred" ? "deferred" : "true0";

  if (
    Object.keys(errors).length === 0 &&
    amount !== null &&
    currentApr !== null &&
    promoApr !== null &&
    postApr !== null &&
    feePct !== null
  ) {
    return {
      valid: true,
      values: {
        amount,
        currentApr,
        promoApr,
        postApr,
        feePct,
        promoMonths,
        promoType,
      },
      errors,
    };
  }
  return { valid: false, errors };
}

// ── Field error rendering ────────────────────────────────────────────────
function renderFieldError(field: FieldName, message: string | null): void {
  const errEl = $(`tc-error-${field}`);
  const inputEl = $(`tc-input-${field}`);
  if (errEl) {
    errEl.textContent = message || "";
    errEl.style.display = message ? "" : "none";
  }
  if (inputEl) {
    if (message) inputEl.setAttribute("aria-invalid", "true");
    else inputEl.removeAttribute("aria-invalid");
  }
}

function showResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "block";
}

function hideResults(): void {
  const r = $("tc-results");
  if (r) r.style.display = "none";
  clearChart(CHART_ID);
}

// ── Compute ──────────────────────────────────────────────────────────────
interface ComputeResult {
  transferFee: number;
  interestAvoided: number;
  promoInterest: number;
  monthlyToClear: number;
  remainingAtEnd: number;
  retroactiveInterest: number;
  netSavings: number;
  stayPut: ChartSeries;
  afterTransfer: ChartSeries;
}

function compute(v: Inputs): ComputeResult {
  const { amount, currentApr, promoApr, postApr, feePct, promoMonths, promoType } = v;
  const N = promoMonths;

  const transferFee = amount * feePct;

  // Daily-compounded interest over the promo window (N 30-day months).
  const interestAvoided = amount * (Math.pow(1 + currentApr / 365, N * 30) - 1);
  const promoInterest = amount * (Math.pow(1 + promoApr / 365, N * 30) - 1);

  // Fixed payment that fully clears the balance within the promo window — the
  // number to actually beat the promo deadline (amortized at the promo rate).
  const r = monthlyRateFromDailyCompounding(promoApr);
  const monthlyToClear =
    r < 0.0001 ? amount / N : (amount * r) / (1 - Math.pow(1 + r, -N));

  // Deferred interest uses the shadow APR on the minimum-payment trajectory:
  // "you made only minimums during the promo, then got hit retroactively."
  const shadowApr =
    promoType === "deferred" && postApr > 0 ? postApr : undefined;
  const promoSim = simulateMinimumPayoff({
    balance: amount,
    apr: promoApr,
    months: N,
    shadowApr,
  });
  const remainingAtEnd = promoSim.endBalance;
  const retroactiveInterest =
    promoType === "deferred" ? promoSim.shadowInterest : 0;

  // Deferred retroactive interest only triggers if a balance remains when the
  // promo ends. On a full payoff (remainingAtEnd === 0) it is never charged.
  const netSavings =
    interestAvoided -
    promoInterest -
    transferFee -
    (remainingAtEnd > 0 ? retroactiveInterest : 0);

  // ── Chart series ─────────────────────────────────────────────────────────
  // Series A "Stay put": minimum-payoff at the current APR, over the same
  // window as the after-transfer line so the gap is visible.
  const windowMonths = Math.max(N + 6, 18);
  const stayPutSim = simulateMinimumPayoff({
    balance: amount,
    apr: currentApr,
    months: windowMonths,
  });

  // Series B "After transfer": promo APR during the window (minimum payments),
  // then the post-promo APR on whatever's left until the window ends.
  const afterMonths = seriesFromMonths(promoSim.months);
  // Continue the after-transfer trajectory at the post-promo rate.
  if (remainingAtEnd > 0 && windowMonths > N) {
    const postSim = simulateMinimumPayoff({
      balance: remainingAtEnd,
      apr: postApr,
      months: windowMonths - N,
    });
    for (const m of postSim.months) {
      afterMonths.push({ month: N + m.month, balance: m.closingBalance });
    }
  }

  const stayPut: ChartSeries = {
    label: t("calculators.balanceTransfer.series_stay_put", "Stay put"),
    color: "#94A3B8",
    points: seriesFromMonths(stayPutSim.months),
  };
  const afterTransfer: ChartSeries = {
    label: t("calculators.balanceTransfer.series_after_transfer", "After transfer"),
    color: "#00876B",
    points: afterMonths,
  };

  return {
    transferFee,
    interestAvoided,
    promoInterest,
    monthlyToClear,
    remainingAtEnd,
    retroactiveInterest,
    netSavings,
    stayPut,
    afterTransfer,
  };
}

// ── Render ───────────────────────────────────────────────────────────────
function renderResults(v: Inputs, r: ComputeResult): void {
  const negative = r.netSavings < 0;

  const heroEl = $("tc-result-hero");
  if (heroEl) {
    heroEl.classList.toggle("is-negative", negative);
    const savingsStr = `<strong>${formatCurrency(Math.abs(r.netSavings))}</strong>`;
    heroEl.innerHTML = negative
      ? t(
          "calculators.balanceTransfer.result_net_cost",
          "This transfer costs you {{cost}} more",
          { cost: savingsStr }
        )
      : t(
          "calculators.balanceTransfer.result_net_savings",
          "Net savings: {{savings}}",
          { savings: savingsStr }
        );
  }

  const avoidedEl = $("tc-result-interest-avoided");
  if (avoidedEl) {
    avoidedEl.innerHTML =
      t("calculators.balanceTransfer.metric_interest_avoided", "Interest avoided") +
      `: <strong>${formatCurrency(r.interestAvoided)}</strong>`;
  }

  const feeEl = $("tc-result-fee");
  if (feeEl) {
    feeEl.innerHTML =
      t("calculators.balanceTransfer.metric_transfer_fee", "Transfer fee") +
      `: <strong>${formatCurrency(r.transferFee)}</strong>`;
  }

  const clearEl = $("tc-result-monthly-to-clear");
  if (clearEl) {
    clearEl.innerHTML =
      t(
        "calculators.balanceTransfer.metric_monthly_to_clear",
        "Pay {{amount}}/mo to clear it in time",
        { amount: `<strong>${formatCurrency(r.monthlyToClear)}</strong>` }
      );
  }

  const remEl = $("tc-result-remaining");
  if (remEl) {
    remEl.innerHTML =
      t("calculators.balanceTransfer.metric_remaining", "Left at promo end") +
      `: <strong>${formatCurrency(r.remainingAtEnd)}</strong>`;
  }

  // Chart
  const svg = $(CHART_ID) as unknown as SVGSVGElement | null;
  if (svg)
    renderMultiSeriesChart(
      svg,
      [r.stayPut, r.afterTransfer],
      t("calculators.balanceTransfer.stillOwing", "still owing")
    );

  // Warning banner: negative net savings OR a deferred retroactive charge.
  const banner = $("tc-warn-banner");
  if (banner) {
    const showWarn = negative || (v.promoType === "deferred" && r.retroactiveInterest > 0);
    banner.style.display = showWarn ? "block" : "none";
    if (showWarn) {
      const headEl = $("tc-warn-heading");
      const bodyEl = $("tc-warn-body");
      if (v.promoType === "deferred" && r.retroactiveInterest > 0 && r.remainingAtEnd > 0.5) {
        if (headEl)
          headEl.textContent = t(
            "calculators.balanceTransfer.warn_deferred_heading",
            "Deferred-interest trap ahead"
          );
        if (bodyEl)
          bodyEl.innerHTML = t(
            "calculators.balanceTransfer.warn_deferred_body",
            "On minimum payments you'd still owe {{remaining}} when the promo ends — triggering a retroactive interest charge of {{retro}} on the original balance. Pay it off in full before the deadline to avoid it.",
            {
              remaining: formatCurrency(r.remainingAtEnd),
              retro: `<strong>${formatCurrency(r.retroactiveInterest)}</strong>`,
            }
          );
      } else {
        if (headEl)
          headEl.textContent = t(
            "calculators.balanceTransfer.warn_heading",
            "This transfer may cost you more than it saves"
          );
        if (bodyEl)
          bodyEl.innerHTML = t(
            "calculators.balanceTransfer.warn_body",
            "After the {{fee}} transfer fee, this transfer comes out behind by {{cost}}.",
            {
              fee: formatCurrency(r.transferFee),
              cost: `<strong>${formatCurrency(Math.abs(r.netSavings))}</strong>`,
            }
          );
      }
    }
  }

  gtag("event", "calc_result_shown", {
    calculator: CALC_ID,
    placement: "calculator",
    promo_type: v.promoType,
    promo_months: v.promoMonths,
    net_positive: r.netSavings >= 0 ? "yes" : "no",
    lang: getLang(),
  });
}

// ── Run / debounce ───────────────────────────────────────────────────────
let recalcTimer: number | null = null;
let gaInputTimer: number | null = null;
let lastChangedField: string | null = null;

function run(): void {
  const raw = readInputs();
  const v = validate(raw);

  (["amount", "currentApr", "promoApr", "postApr", "fee"] as FieldName[]).forEach(
    (f) => renderFieldError(f, v.errors[f] || null)
  );

  if (!v.valid || !v.values) {
    hideResults();
    return;
  }

  try {
    const result = compute(v.values);
    showResults();
    renderResults(v.values, result);
  } catch (err) {
    const msg =
      err instanceof Error ? err.message.slice(0, 80) : String(err).slice(0, 80);
    hideResults();
    const errBox = $("tc-runtime-error");
    if (errBox) {
      errBox.textContent = t(
        "calculators.balanceTransfer.runtime_error",
        "Something went wrong. Try refreshing the page."
      );
      errBox.style.display = "block";
    }
    gtag("event", "calc_error", { calculator: CALC_ID, placement: "calculator", message: msg, lang: getLang() });
  }
}

function scheduleRecalc(): void {
  if (recalcTimer !== null) window.clearTimeout(recalcTimer);
  recalcTimer = window.setTimeout(() => {
    recalcTimer = null;
    run();
  }, DEBOUNCE_MS);
}

function scheduleGaInputEvent(): void {
  if (gaInputTimer !== null) window.clearTimeout(gaInputTimer);
  gaInputTimer = window.setTimeout(() => {
    gaInputTimer = null;
    if (lastChangedField) {
      gtag("event", "calc_input_change", {
        calculator: CALC_ID,
        placement: "calculator",
        field: lastChangedField,
        lang: getLang(),
      });
    }
  }, GA_INPUT_DEBOUNCE_MS);
}

// ── Public API ───────────────────────────────────────────────────────────
export function attachCalculator(): void {
  // Text/number inputs
  (["amount", "currentApr", "promoApr", "postApr", "fee"] as FieldName[]).forEach(
    (field) => {
      const el = $(`tc-input-${field}`) as HTMLInputElement | null;
      if (!el) return;
      el.addEventListener("input", () => {
        lastChangedField = field;
        const errBox = $("tc-runtime-error");
        if (errBox) errBox.style.display = "none";
        scheduleRecalc();
        scheduleGaInputEvent();
      });
    }
  );

  // Radio groups (pills + toggle) — recalc immediately on change.
  document
    .querySelectorAll<HTMLInputElement>('input[name="promoPeriod"], input[name="promoType"]')
    .forEach((el) => {
      el.addEventListener("change", () => {
        lastChangedField = el.name;
        scheduleRecalc();
        scheduleGaInputEvent();
      });
    });

  // CTA click tracking
  document.querySelectorAll<HTMLAnchorElement>("[data-calc-cta]").forEach((a) => {
    a.addEventListener("click", () => {
      const store = a.getAttribute("data-calc-cta") || "unknown";
      const lang = getLang();
      // Keep calc_cta_click (it has GA history). The unified
      // `app_store_click` event is now owned solely by /js/store-tracking.js
      // (the global delegated listener) to avoid double-counting.
      gtag("event", "calc_cta_click", { calculator: CALC_ID, placement: "calculator", store, lang });
    });
  });

  const calcButton = $("tc-calculate-button") as HTMLButtonElement | null;
  if (calcButton) {
    calcButton.addEventListener("click", (e) => {
      e.preventDefault();
      run();
    });
  }

  const form = $("tc-form") as HTMLFormElement | null;
  if (form) {
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      run();
    });
  }

  gtag("event", "calc_view", { calculator: CALC_ID, placement: "calculator", lang: getLang() });

  // Only auto-run once the user has entered the primary required field, so the
  // page doesn't paint a validation error on first load (the amount field is
  // placeholder-only). Matches the true-cost reference behavior.
  if (readInputs().amount) run();
}
